import { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Target, Lightbulb, Anchor, Clapperboard, FileText, Megaphone, Hash,
  CalendarPlus, Copy, Image as ImageIcon, Loader2, BarChart3, Clock, Share, Download, ExternalLink,
} from "lucide-react";
import { PLATFORM_STYLE } from "@/lib/agents";

const copyText = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copiado!"); };

function Block({ icon: Icon, label, color, children, testid, copyable }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="overline" style={{ color }}>{label}</span>
        </div>
        {copyable != null && (
          <button onClick={() => copyText(copyable)} className="text-slate-500 hover:text-emerald-400 transition-colors" data-testid={`copy-${testid}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function PlatformTab({ data, platform }) {
  if (!data) return <p className="text-sm text-slate-500">Sem adaptação.</p>;
  const st = PLATFORM_STYLE[platform];
  return (
    <div className="space-y-3">
      <Badge className={`${st.badge} border`}>{data.formato || platform}</Badge>
      <Block icon={Share} label="Adaptação" color={st.dot}>{data.adaptacao}</Block>
      <Block icon={FileText} label="Legenda adaptada" color={st.dot} copyable={data.legenda}>{data.legenda}</Block>
    </div>
  );
}

export default function PackageView({ pkg, brand, onScheduled }) {
  const fmtFor = (p) => p === "TikTok" ? (pkg.tiktok?.formato || "Vídeo") : p === "Facebook" ? (pkg.facebook?.formato || "Post") : (pkg.instagram?.formato || "Reel");
  const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
  const [img, setImg] = useState(pkg.image_url || null);
  const [genning, setGenning] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [sched, setSched] = useState({ plataforma: "Instagram", formato: fmtFor("Instagram"), dia: tomorrow(), hora: "19:00" });
  const pickPlatform = (p) => setSched((s) => ({ ...s, plataforma: p, formato: fmtFor(p) }));

  const handleFor = (p) => ((p === "Instagram" ? brand?.instagram_handle : p === "TikTok" ? brand?.tiktok_handle : brand?.facebook_handle) || "");
  const netUrl = (p, h) => { const u = (h || "").replace(/^@/, ""); return p === "TikTok" ? `https://www.tiktok.com/@${u}` : p === "Facebook" ? `https://www.facebook.com/${u}` : `https://www.instagram.com/${u}`; };
  const postText = (p) => { const d = p === "Instagram" ? pkg.instagram : p === "TikTok" ? pkg.tiktok : pkg.facebook; const cap = (d && d.legenda) || pkg.legenda || ""; return `${cap}\n\n${(pkg.hashtags || []).join(" ")}`.trim(); };
  const downloadImg = () => { if (!img) { toast.error("Gere a arte primeiro (botão roxo)."); return; } const a = document.createElement("a"); a.href = img; a.download = `${(pkg.brand_nome || "post").replace(/\s+/g, "_")}.png`; document.body.appendChild(a); a.click(); a.remove(); };

  const [igConfigured, setIgConfigured] = useState(false);
  const [publishing, setPublishing] = useState(false);
  useEffect(() => {
    api.get("/instagram/status").then((r) => setIgConfigured(!!r.data.configured)).catch((e) => console.debug("ig status indisponível", e));
  }, []);

  const publishInstagram = async () => {
    if (!img) { toast.error("Gere a arte com IA primeiro."); return; }
    setPublishing(true);
    try {
      const image_url = `${API}/public/image/${pkg.package_id}`;
      const res = await api.post("/instagram/publish", { package_id: pkg.package_id, image_url, caption: postText("Instagram") });
      toast.success("Publicado no Instagram! ID: " + res.data.media_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao publicar no Instagram.");
    } finally { setPublishing(false); }
  };

  const genImage = async () => {
    setGenning(true);
    try {
      const res = await api.post("/generate-image", { prompt: pkg.image_prompt || pkg.ideia });
      setImg(res.data.image_url);
      await api.put(`/packages/${pkg.package_id}/image`, { image_url: res.data.image_url });
      toast.success("Imagem gerada com IA!");
    } catch (e) {
      toast.error("Falha ao gerar imagem.");
    } finally { setGenning(false); }
  };

  const submitSchedule = async () => {
    if (!sched.dia) { toast.error("Escolha uma data."); return; }
    try {
      await api.post("/calendar", {
        package_id: pkg.package_id, brand_id: pkg.brand_id,
        dia: sched.dia, hora: sched.hora, plataforma: sched.plataforma,
        formato: sched.formato, tema: pkg.ideia?.slice(0, 80) || "",
        hook: pkg.hook || "", cta: pkg.cta || "", objetivo: pkg.objetivo || "", status: "agendado",
      });
      toast.success("Adicionado ao calendário editorial!");
      setSchedOpen(false);
      onScheduled?.();
    } catch (e) { toast.error("Erro ao agendar."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button data-testid="btn-gen-image" onClick={genImage} disabled={genning} size="sm"
          className="bg-violet-500 hover:bg-violet-400 text-white hover:brightness-110 transition-all">
          {genning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
          {img ? "Regenerar arte" : "Gerar arte com IA"}
        </Button>
        <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-schedule-package" size="sm" variant="outline"
              className="border-slate-700 hover:border-emerald-500/50">
              <CalendarPlus className="w-4 h-4 mr-2" /> Agendar no calendário
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-md">
            <DialogHeader><DialogTitle className="font-display">Agendar no calendário</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 text-sm mb-1.5 block">Plataforma</Label>
                <Select value={sched.plataforma} onValueChange={pickPlatform}>
                  <SelectTrigger data-testid="sched-plataforma" className="bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    <SelectItem value="Instagram">Instagram</SelectItem>
                    <SelectItem value="TikTok">TikTok</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-1.5 block">Formato</Label>
                <Select value={sched.formato} onValueChange={(v) => setSched({ ...sched, formato: v })}>
                  <SelectTrigger data-testid="sched-formato" className="bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {["Reel", "Carrossel", "Story", "Post", "Vídeo", "Texto"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-sm mb-1.5 block">Data</Label>
                  <Input data-testid="sched-data" type="date" value={sched.dia}
                    onChange={(e) => setSched({ ...sched, dia: e.target.value })}
                    className="bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm mb-1.5 block">Horário</Label>
                  <Input data-testid="sched-hora" type="time" value={sched.hora}
                    onChange={(e) => setSched({ ...sched, hora: e.target.value })}
                    className="bg-slate-900/60 border-slate-700" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-confirm-schedule" onClick={submitSchedule}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold w-full">
                Agendar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-publish-package" size="sm"
              className="bg-pink-500 hover:bg-pink-400 text-white hover:brightness-110 transition-all">
              <Share className="w-4 h-4 mr-2" /> Divulgar nas redes
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Kit de publicação</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-400 -mt-2">Texto pronto por rede. Copie, baixe a arte e abra a plataforma para publicar.</p>
            <Tabs defaultValue="Instagram">
              <TabsList className="bg-slate-900/60 border border-slate-800 w-full">
                <TabsTrigger value="Instagram" data-testid="pub-tab-instagram" className="flex-1">Instagram</TabsTrigger>
                <TabsTrigger value="TikTok" data-testid="pub-tab-tiktok" className="flex-1">TikTok</TabsTrigger>
                <TabsTrigger value="Facebook" data-testid="pub-tab-facebook" className="flex-1">Facebook</TabsTrigger>
              </TabsList>
              {["Instagram", "TikTok", "Facebook"].map((p) => (
                <TabsContent key={p} value={p} className="space-y-3 mt-4">
                  {handleFor(p) && (
                    <p className="text-xs font-mono text-slate-400">Conta: <span className="text-emerald-300">{handleFor(p).startsWith("@") ? handleFor(p) : "@" + handleFor(p)}</span></p>
                  )}
                  <div data-testid={`pub-text-${p.toLowerCase()}`}
                    className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-sm text-slate-200 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {postText(p)}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid={`pub-copy-${p.toLowerCase()}`} onClick={() => copyText(postText(p))} size="sm" variant="outline" className="border-slate-700 hover:border-emerald-500/50">
                      <Copy className="w-4 h-4 mr-2" /> Copiar texto
                    </Button>
                    <Button data-testid={`pub-download-${p.toLowerCase()}`} onClick={downloadImg} size="sm" variant="outline" className="border-slate-700 hover:border-emerald-500/50">
                      <Download className="w-4 h-4 mr-2" /> Baixar arte
                    </Button>
                  </div>
                  <Button data-testid={`pub-open-${p.toLowerCase()}`} onClick={() => window.open(netUrl(p, handleFor(p)), "_blank")} size="sm"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all">
                    <ExternalLink className="w-4 h-4 mr-2" /> Abrir {p} para publicar
                  </Button>
                  {p === "Instagram" && (igConfigured ? (
                    <Button data-testid="pub-auto-instagram" onClick={publishInstagram} disabled={publishing || !img} size="sm"
                      className="w-full bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 text-white font-semibold hover:brightness-110 transition-all">
                      {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share className="w-4 h-4 mr-2" />} Publicar agora (API oficial)
                    </Button>
                  ) : (
                    <p className="text-xs text-amber-300/90 border border-amber-700/40 bg-amber-950/20 rounded-lg p-2" data-testid="ig-not-configured">
                      Publicação automática disponível após conectar o Instagram (credenciais da Meta).
                    </p>
                  ))}
                  <p className="text-xs text-slate-500">Dica VIP: baixe a arte, abra o {p} na sua conta e cole o texto já copiado.</p>
                </TabsContent>
              ))}
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {img && (
        <div className="rounded-xl overflow-hidden border border-slate-800">
          <img src={img} alt="Arte gerada" className="w-full object-cover" data-testid="package-image" />
        </div>
      )}

      <Tabs defaultValue="geral">
        <TabsList className="bg-slate-900/60 border border-slate-800 flex-wrap h-auto">
          <TabsTrigger value="geral" data-testid="tab-pkg-geral">Pacote</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="tab-pkg-instagram">Instagram</TabsTrigger>
          <TabsTrigger value="tiktok" data-testid="tab-pkg-tiktok">TikTok</TabsTrigger>
          <TabsTrigger value="facebook" data-testid="tab-pkg-facebook">Facebook</TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="space-y-3 mt-4">
          <Block icon={Target} label="🎯 Objetivo" color="#10B981">{pkg.objetivo}</Block>
          <Block icon={Lightbulb} label="💡 Ideia" color="#F59E0B">{pkg.ideia}</Block>
          <Block icon={Anchor} label={`🪝 Hook${pkg.hook_tipo ? " · " + pkg.hook_tipo : ""}`} color="#EC4899" copyable={pkg.hook}>{pkg.hook}</Block>
          <Block icon={Clapperboard} label="🎬 Roteiro" color="#6366F1" copyable={pkg.roteiro}>{pkg.roteiro}</Block>
          <Block icon={FileText} label="📝 Legenda" color="#06B6D4" copyable={pkg.legenda}>{pkg.legenda}</Block>
          <Block icon={Megaphone} label="📢 CTA" color="#10B981" copyable={pkg.cta}>{pkg.cta}</Block>
          <Block icon={Hash} label="#️⃣ Hashtags" color="#3B82F6" copyable={(pkg.hashtags || []).join(" ")}>
            <div className="flex flex-wrap gap-1.5 font-mono text-xs">
              {(pkg.hashtags || []).map((h, i) => <span key={`${h}-${i}`} className="px-2 py-0.5 rounded bg-slate-800 text-blue-300">{h}</span>)}
            </div>
          </Block>
          <div className="grid sm:grid-cols-2 gap-3">
            <Block icon={Clock} label="📅 Data/horário sugerido" color="#F59E0B">{pkg.data_horario_sugerido}</Block>
            <Block icon={BarChart3} label="📊 Métricas para acompanhar" color="#06B6D4">
              <ul className="list-disc list-inside space-y-0.5">
                {(pkg.metricas || []).map((m, i) => <li key={`${m}-${i}`}>{m}</li>)}
              </ul>
            </Block>
          </div>
        </TabsContent>
        <TabsContent value="instagram" className="mt-4"><PlatformTab data={pkg.instagram} platform="Instagram" /></TabsContent>
        <TabsContent value="tiktok" className="mt-4"><PlatformTab data={pkg.tiktok} platform="TikTok" /></TabsContent>
        <TabsContent value="facebook" className="mt-4"><PlatformTab data={pkg.facebook} platform="Facebook" /></TabsContent>
      </Tabs>
    </div>
  );
}
